import { createContext } from "react";

export const ActiveThemeContext = createContext({ theme: null, status: "loading" });

export const themeHasSections = (theme) =>
  Array.isArray(theme?.sections) && theme.sections.length > 0;

const ACTIVE_KEY = "ecom_active_theme";
const LIST_KEY = "ecom_theme_list";

const recordKey = (themeKey) => `ecom_theme_${themeKey}`;

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota errors. The next successful response still updates the screen.
  }
};

export const readThemeRecord = (themeKey) => {
  if (!themeKey) return null;
  const theme = readJson(recordKey(themeKey));
  if (!theme || theme.theme_key !== themeKey) return null;
  return theme;
};

export const writeThemeRecord = (theme) => {
  if (!theme?.theme_key) return;
  writeJson(recordKey(theme.theme_key), theme);
};

export const themeForThisVisit = (theme) => {
  if (!theme?.theme_key || !theme.settings || typeof window === "undefined") return null;
  const preview = new URLSearchParams(window.location.search).get("preview_theme");
  if (preview) return theme.is_preview && theme.theme_key === preview ? theme : null;
  if (theme.is_preview) return null;
  return theme;
};

export const readActiveThemeCache = () => {
  const theme = readJson(ACTIVE_KEY);
  if (!theme?.theme_key || !theme.settings) return null;
  return theme;
};

export const writeActiveThemeCache = (theme) => {
  if (!theme?.theme_key || theme.is_preview) return;
  let next = theme;
  if (!themeHasSections(next)) {
    const previous = readJson(ACTIVE_KEY);
    if (previous?.theme_key === next.theme_key && themeHasSections(previous)) {
      next = { ...next, sections: previous.sections };
    }
  }
  writeThemeRecord(next);
  writeJson(ACTIVE_KEY, next);
};

export const readThemeListCache = () => {
  const themes = readJson(LIST_KEY);
  return Array.isArray(themes) && themes.length > 0 ? themes : null;
};

export const writeThemeListCache = (themes) => {
  if (!Array.isArray(themes) || themes.length === 0) return;
  writeJson(LIST_KEY, themes);
};
